import { Users } from 'lucide-react';

import type { GuideModule } from '../types';

/**
 * Every quoted label was re-checked against
 * `routes/settings/users/users-list-page.tsx`, its modals
 * (`UserInviteModal`, `UserEditModal`, `UserSessionsModal`,
 * `UserResetTotpModal`) and `auth/accept-invitation-page.tsx`.
 */
export const USERS_MODULE: GuideModule = {
  id: 'users',
  title: 'Usuarios',
  icon: Users,
  requiredPermission: 'user.read',
  priority: 2,
  summary:
    'Acá gestionás las cuentas internas del sistema: invitás gente nueva, editás sus datos y roles, revisás y cerrás sus sesiones activas, reiniciás su verificación en dos pasos y desactivás o reactivás su acceso. Desactivar corta el acceso de inmediato.',
  steps: [
    {
      title: 'Invitar a alguien nuevo',
      description:
        'Hacé clic en "Invitar usuario" y completá "Email" y "Nombre completo". En el bloque "Roles" marcá uno o más: un checkbox aparece deshabilitado cuando ese rol incluye algún permiso que vos mismo no tenés — no podés otorgar más de lo que tenés. El botón "Invitar" queda deshabilitado hasta que marqués al menos un rol.',
      keyTerms: ['invitacion', 'rol'],
    },
    {
      title: 'Qué recibe la persona invitada',
      description:
        'Recibe un email con un enlace. Al abrirlo llega a "Activá tu cuenta interna", elige su "Contraseña" y hace clic en "Activar cuenta"; recién ahí su usuario pasa de "Invitado" a "Activo" y puede iniciar sesión. Si el enlace es inválido o ya venció, la pantalla lo indica y hay que pedirle a quien invitó que envíe una invitación nueva.',
      keyTerms: ['invitacion'],
    },
    {
      title: 'Si no hay correo saliente: compartir el enlace',
      description:
        'Cuando el correo saliente no está configurado, al invitar o reenviar se abre la ventana "Enlace de activación" con el enlace en el campo "Enlace de activación" y el botón "Copiar". Entregáselo a la persona por otro medio. Solo se muestra en ese momento: si cerrás la ventana con "Listo" sin copiarlo, usá "Reenviar invitación". Si la persona invitada tendrá permisos que vos no tenés, el sistema no te muestra el enlace y hay que pedirle a un `SUPER_ADMIN` que reenvíe la invitación.',
      keyTerms: ['invitacion'],
    },
    {
      title: 'Reenviar una invitación',
      description:
        'Mientras el usuario siga en estado "Invitado", su fila tiene la acción "Reenviar invitación", que genera un enlace nuevo. Hay un límite de invitaciones por hora por cada persona que invita o reenvía: si lo superás, el sistema rechaza el pedido con "Enviaste demasiadas invitaciones. Esperá unos minutos y volvé a intentar.".',
      keyTerms: ['invitacion'],
    },
    {
      title: 'Editar nombre y roles',
      description:
        'La acción "Editar" abre un formulario con "Nombre completo", "Zonas" y "Roles". El email nunca se edita desde acá. Los checkboxes de "Roles" siguen la misma regla que al invitar: se deshabilitan si el rol excede tus propios permisos. Guardar solo envía los cambios de la sección que realmente modificaste.',
      keyTerms: ['rol'],
    },
    {
      title: 'Revisar y cerrar sesiones',
      description:
        'La acción "Sesiones" lista cada sesión activa de ese usuario con la fecha en que se creó. "Revocar" cierra una sesión puntual; "Revocar todas" cierra todas de una vez. Si el usuario no tiene ninguna sesión activa, la lista lo indica.',
      keyTerms: ['sesion'],
    },
    {
      title: 'Reiniciar la verificación en dos pasos',
      description:
        'Si alguien perdió su teléfono o necesita reconfigurar su verificación en dos pasos, usá "Reiniciar verificación". Pide una confirmación explícita antes de aplicarse; la persona va a tener que volver a configurar la verificación en dos pasos la próxima vez que ingrese.',
      keyTerms: ['verificacion-en-dos-pasos'],
    },
    {
      title: 'Desactivar y reactivar',
      description:
        'La acción "Desactivar" pide confirmación y corta el acceso de inmediato: cierra todas las sesiones activas del usuario en la misma operación. "Reactivar" devuelve el acceso a un usuario "Inactivo", sin restaurar sus sesiones anteriores. No podés desactivarte a vos mismo, y el sistema tampoco te deja dejarlo sin ningún Superadministrador activo: la acción que llevaría a ese estado se rechaza.',
      keyTerms: [],
    },
  ],
  states: [],
  faq: [
    {
      question: '¿Por qué un checkbox de rol aparece deshabilitado al invitar o editar?',
      answer:
        'Porque ese rol incluye al menos un permiso que vos mismo no tenés. No podés otorgarle a otra persona más permisos de los que tenés asignados.',
    },
    {
      question: '¿Puedo desactivarme a mí mismo?',
      answer: 'No. La acción de desactivar no está disponible sobre tu propio usuario.',
    },
    {
      question: '¿Qué pasa si intento dejar el sistema sin ningún Superadministrador activo?',
      answer:
        'El sistema lo rechaza. Siempre tiene que quedar al menos un usuario activo con el rol Superadministrador, ya sea al desactivarlo o al quitarle ese rol.',
    },
    {
      question: '¿Reactivar un usuario le restaura las sesiones que tenía antes de desactivarlo?',
      answer: 'No. Reactivar solo devuelve el acceso; la persona tiene que iniciar sesión de nuevo.',
    },
    {
      question: '¿Hay un límite para las invitaciones que puedo enviar?',
      answer:
        'Sí, un límite por hora por cada persona que invita o reenvía. Si lo alcanzás, esperá unos minutos antes de volver a intentar.',
    },
  ],
  tips: [
    'Antes de invitar, confirmá con la persona qué roles necesita: agregar un rol de más siempre se puede revertir después desde "Editar", pero es mejor empezar con lo justo.',
    'Si alguien reporta que no puede iniciar sesión después de mucho tiempo sin usar el sistema, revisá su estado antes que nada: puede estar "Inactivo".',
  ],
};
